import { Box } from '@mui/material';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ISKIERKA — mikrowykres w wierszu tabeli (warstwa wspólna nr 5)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * KONTRAKT: iskierka dostaje SERIĘ LICZB i sama wyprowadza kierunek. Nie przyjmuje „zielona",
 * „w górę" ani „dodatnia" jako gotowego werdyktu — bo wtedy przestaje być wykresem, a staje się
 * ozdobą pod czyjąś tezą. Jeśli ktoś poda ciąg malejący i etykietę „wzrost", iskierka narysuje
 * spadek. Tak ma być.
 *
 * SZEŚĆ STANÓW, TRZY RÓŻNE POWODY BRAKU RYSUNKU — i nie wolno ich zlewać:
 *   BRAK_ZRODLA   `null` — nie pytaliśmy albo źródło nie odpowiedziało
 *   PUSTA         zero punktów — pytaliśmy, nie było czego liczyć
 *   ZA_MALO       jeden punkt — jest pomiar, ale przebiegu z jednego punktu nie ma
 * We wszystkich trzech rysujemy KRESKĘ, nigdy poziomej linii przy zerze. Linia na zerze mówi
 * „zmierzyliśmy i przez cały okres było zero" — to zupełnie inne zdanie niż „nie wiemy".
 */

export type StanIskierki = 'ROSNACA' | 'MALEJACA' | 'PLASKA' | 'ZA_MALO' | 'PUSTA' | 'BRAK_ZRODLA';

export interface OcenaIskierki {
  stan: StanIskierki;
  /** Zmiana od pierwszego do ostatniego punktu w procentach. `null`, gdy nie da się policzyć. */
  zmianaPct: number | null;
  opis: string;
}

/** Próg martwej strefy: poniżej tego uznajemy przebieg za płaski, a nie za trend. */
const PROG_PLASKI = 1;

export function ocenIskierke(wartosci: readonly number[] | null | undefined): OcenaIskierki {
  if (wartosci == null) return { stan: 'BRAK_ZRODLA', zmianaPct: null, opis: 'Źródło nie zwróciło przebiegu.' };
  if (wartosci.length === 0) return { stan: 'PUSTA', zmianaPct: null, opis: 'Przebieg jest pusty — nie było czego mierzyć.' };
  if (wartosci.length < 2)
    return { stan: 'ZA_MALO', zmianaPct: null, opis: 'Jeden punkt pomiaru — przebiegu z niego nie ma.' };

  const pierwszy = wartosci[0]!;
  const ostatni = wartosci[wartosci.length - 1]!;
  // Zmiana względna wymaga niezerowego punktu odniesienia. Przy zerze na starcie procent
  // jest nieokreślony i podanie „+∞%" albo „+100%" byłoby wymysłem.
  const zmianaPct = pierwszy === 0 ? null : ((ostatni - pierwszy) / Math.abs(pierwszy)) * 100;

  const stan: StanIskierki =
    zmianaPct == null
      ? ostatni > 0
        ? 'ROSNACA'
        : 'PLASKA'
      : zmianaPct > PROG_PLASKI
        ? 'ROSNACA'
        : zmianaPct < -PROG_PLASKI
          ? 'MALEJACA'
          : 'PLASKA';

  return {
    stan,
    zmianaPct,
    opis: `Przebieg ${wartosci.length} okresów${zmianaPct == null ? '' : `, zmiana ${zmianaPct > 0 ? '+' : ''}${zmianaPct.toFixed(1)}%`}.`,
  };
}

const BARWA: Record<StanIskierki, string> = {
  ROSNACA: 'var(--sukces)',
  MALEJACA: 'var(--krytyczny)',
  PLASKA: 'var(--neutralny)',
  ZA_MALO: 'var(--neutralny)',
  PUSTA: 'var(--neutralny)',
  BRAK_ZRODLA: 'var(--neutralny)',
};

export function Iskierka({
  wartosci,
  szerokosc = 96,
  wysokosc = 26,
  /** Wymuszenie barwy tam, gdzie kierunek NIE jest oceną (np. wolumen). */
  kolor,
}: {
  wartosci: readonly number[] | null | undefined;
  szerokosc?: number;
  wysokosc?: number;
  kolor?: string;
}) {
  const ocena = ocenIskierke(wartosci);

  if (ocena.stan === 'ZA_MALO' || ocena.stan === 'PUSTA' || ocena.stan === 'BRAK_ZRODLA') {
    return (
      <Box component="span" className="iskierka-pusta" title={ocena.opis} aria-label={ocena.opis} role="img">
        —
      </Box>
    );
  }

  const v = wartosci as readonly number[];
  const max = Math.max(...v);
  const min = Math.min(...v);
  const zakres = max - min || 1;
  const yy = (x: number) => wysokosc - ((x - min) / zakres) * (wysokosc - 4) - 2;
  const punkty = v.map((x, i) => `${((i / (v.length - 1)) * szerokosc).toFixed(1)},${yy(x).toFixed(1)}`).join(' ');
  const barwa = kolor ?? BARWA[ocena.stan];

  return (
    <svg viewBox={`0 0 ${szerokosc} ${wysokosc}`} width={szerokosc} height={wysokosc} role="img" style={{ flex: 'none' }}>
      <title>{ocena.opis}</title>
      <polyline points={punkty} fill="none" stroke={barwa} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={szerokosc - 1.5} cy={yy(v[v.length - 1]!).toFixed(1)} r="2.4" fill={barwa} />
    </svg>
  );
}
